import { supabase } from './supabaseClient';

// Sign up function and insert user into 'profiles' table
export const signUp = async (email, password, metadata = {}) => {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: Object.keys(metadata || {}).length ? { data: metadata } : undefined,
  });

  if (error) {
    throw error;
  }

  const user = data.user;
  const session = data.session;
  const hasSession = !!session;

  // Insert or upsert a profile row (only when a session exists; otherwise wait for confirmation link)
  if (user?.id && hasSession) {
    const { error: insertError } = await supabase
      .from('profiles')
      .upsert([{ id: user.id, email, created_at: new Date() }], { onConflict: 'id' });

    if (insertError) {
      // Ignore profile insert failures to avoid blocking auth
      console.warn('Profile upsert warning:', insertError.message);
    }
  } else if (user?.id) {
    // No session yet (likely waiting on email confirmation); profile will be created on first signed-in session.
    console.info('Skipping profile upsert until email is confirmed and session is established.');
  }

  return { user, session };
};

// Log in function (for email/password)
export const logIn = async (email, password) => {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });

  if (error) {
    throw error;
  }

  const user = data.user;
  const session = data.session;

  // Retrieve additional user data from the 'profiles' table
  const { data: userData, error: fetchError } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', user.id)
    .maybeSingle();

  if (fetchError) {
    console.error('Error fetching user data from profiles table:', fetchError);
  }

  // If no profile exists (legacy users), create a baseline profile row
  let ensuredProfile = userData;
  if (!userData && user?.id) {
    const { data: createdProfile, error: upsertError } = await supabase
      .from('profiles')
      .upsert([{ id: user.id, email, created_at: new Date() }], { onConflict: 'id' })
      .select()
      .maybeSingle();

    if (upsertError) {
      console.warn('Profile upsert warning on login:', upsertError.message);
    } else {
      ensuredProfile = createdProfile;
    }
  }

  return { user, userData: ensuredProfile, session };
};

// Google Sign-In function (not wired in UI, kept for future use)
export const signInWithGoogle = async () => {
  const { error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
  });

  if (error) {
    throw error;
  }
};
